/**
 * FFmpeg Service - Singleton pour gérer une instance partagée de FFmpeg
 *
 * Charge FFmpeg une seule fois au démarrage de l'application pour éviter :
 * - Les téléchargements multiples depuis le CDN
 * - Les blocages à 20% pendant l'export
 * - Les délais de chargement pendant l'export
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL, fetchFile } from '@ffmpeg/util';

export type FFmpegLoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface FFmpegServiceState {
  status: FFmpegLoadStatus;
  progress: number;
  error?: string;
}

class FFmpegServiceClass {
  private ffmpeg: FFmpeg | null = null;
  private loadPromise: Promise<FFmpeg> | null = null;
  private status: FFmpegLoadStatus = 'idle';
  private listeners: Set<(state: FFmpegServiceState) => void> = new Set();

  /**
   * Obtenir l'instance FFmpeg (la charge si nécessaire)
   */
  async getInstance(): Promise<FFmpeg> {
    if (this.ffmpeg && this.status === 'loaded') {
      return this.ffmpeg;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.load();
    return this.loadPromise;
  }

  /**
   * Charger FFmpeg depuis le CDN
   */
  private async load(): Promise<FFmpeg> {
    if (this.ffmpeg && this.status === 'loaded') {
      return this.ffmpeg;
    }

    try {
      this.status = 'loading';
      this.notifyListeners();

      console.log('[FFmpegService] Chargement de FFmpeg...');
      this.ffmpeg = new FFmpeg();

      // Logs — filter out noisy version/config/library lines
      this.ffmpeg.on('log', ({ message }) => {
        const lower = message.toLowerCase();
        // Skip version banners, library versions, config, and build info
        if (lower.includes('ffmpeg version') || lower.includes('copyright') ||
          lower.includes('built with') || lower.includes('configuration:') ||
          lower.includes('libav') || lower.includes('libsw') || lower.includes('libpost')) {
          return; // Suppress noisy startup logs
        }
        if (lower.includes('error') || lower.includes('warning') || lower.includes('fatal')) {
          console.warn('[FFmpeg]', message);
        } else {
          console.debug('[FFmpeg]', message); // Use debug level for operational logs
        }
      });

      // Progress — use debug to avoid cluttering console
      this.ffmpeg.on('progress', ({ progress }) => {
        console.debug('[FFmpeg Progress]', Math.round(progress * 100) + '%');
      });

      // Charger depuis CDN (Utilisation de jsdelivr comme alternative plus fiable que unpkg)
      const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm';

      console.log('[FFmpegService] Téléchargement des fichiers depuis CDN...');

      const loadPromise = (async () => {
        const coreURL = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');
        const wasmURL = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');
        await this.ffmpeg!.load({ coreURL, wasmURL });
      })();

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout de chargement FFmpeg (cdn trop lent, > 45s)')), 45000);
      });

      await Promise.race([loadPromise, timeoutPromise]);

      this.status = 'loaded';
      this.notifyListeners();
      console.log('[FFmpegService] FFmpeg chargé avec succès ✓');

      return this.ffmpeg;
    } catch (error) {
      this.status = 'error';
      this.notifyListeners();
      console.error('[FFmpegService] Erreur lors du chargement de FFmpeg:', error);
      throw error;
    }
  }

  /**
   * Pré-charger FFmpeg en arrière-plan (non-bloquant)
   */
  async preload(): Promise<void> {
    try {
      await this.getInstance();
    } catch (error) {
      console.error('[FFmpegService] Échec du pré-chargement:', error);
      // Ne pas throw pour ne pas bloquer l'application
    }
  }

  /**
   * Obtenir le statut actuel
   */
  getStatus(): FFmpegServiceState {
    return {
      status: this.status,
      progress: this.status === 'loaded' ? 100 : this.status === 'loading' ? 50 : 0,
    };
  }

  /**
   * Écouter les changements de statut
   */
  subscribe(listener: (state: FFmpegServiceState) => void): () => void {
    this.listeners.add(listener);
    // Retourner une fonction de désabonnement
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notifier tous les listeners
   */
  private notifyListeners(): void {
    const state = this.getStatus();
    this.listeners.forEach(listener => listener(state));
  }

  /**
   * Vérifier si FFmpeg est chargé
   */
  isLoaded(): boolean {
    return this.status === 'loaded';
  }

  /**
   * Marquer l'instance comme terminée (appelé après ffmpeg.terminate())
   * Permet de recharger FFmpeg lors du prochain export
   */
  markTerminated(): void {
    console.log('[FFmpegService] Instance marquée comme terminée, sera rechargée au prochain appel');
    this.ffmpeg = null;
    this.loadPromise = null;
    this.status = 'idle';
  }

  /**
   * Reset (pour tests uniquement)
   */
  reset(): void {
    this.ffmpeg = null;
    this.loadPromise = null;
    this.status = 'idle';
    this.notifyListeners();
  }
  async trimVideo(file: Blob, start: number, duration: number): Promise<Blob> {
    const ffmpeg = await this.getInstance();
    const inputName = 'input_trim.mp4';
    const outputName = 'output_trim.mp4';

    console.log(`[FFmpegService] Trimming video: start=${start}, duration=${duration}`);

    // Écrire le fichier
    await ffmpeg.writeFile(inputName, await fetchFile(file));

    // Exécuter la commande trim
    // Utilisation de re-encodage rapide pour assurer la compatibilité
    await ffmpeg.exec([
      '-ss', start.toString(),
      '-i', inputName,
      '-t', duration.toString(),
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-c:a', 'aac',
      outputName
    ]);

    // Lire le résultat
    const data = await ffmpeg.readFile(outputName);

    // Conversion explicite en Uint8Array pour le Blob
    const uint8Data = new Uint8Array(data as unknown as ArrayBufferLike);
    const blob = new Blob([uint8Data as any], { type: 'video/mp4' });

    // Cleanup
    try {
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);
    } catch (e) {
      console.warn('Erreur cleanup ffmpeg:', e);
    }

    return blob;
  }

  /**
   * Détecter le FPS d'une vidéo via FFmpeg
   */
  async detectFPS(url: string, file?: File): Promise<number> {
    const ffmpeg = await this.getInstance();
    const inputName = 'input_probe.mp4';

    console.log(`[FFmpegService] Detecting FPS for ${url}...`);

    try {
      // 1. Charger le fichier
      // OPTIMIZATION: Use WORKERFS if file object is provided (prevents OOM for large files)
      let mountDir = '';
      let safeFilename = inputName;

      // Validate file object is a proper File with a name property
      const isValidFile = file && file instanceof File && typeof file.name === 'string' && file.name.length > 0;

      if (isValidFile) {
        mountDir = `/mnt_fps_${Date.now()}`;
        try { await ffmpeg.createDir(mountDir); } catch (e) { }

        // Sanitize filename to avoid WORKERFS format errors (spaces, special chars)
        // We create a new File with a safe name but same content
        const ext = file.name.split('.').pop() || 'mp4';
        const safeName = `input.${ext}`;
        const safeFile = new File([file], safeName, { type: file.type });

        await ffmpeg.mount('WORKERFS' as any, { files: [safeFile] }, mountDir);
        safeFilename = safeName;
      } else {
        const response = await fetch(url);
        const blob = await response.blob();
        await ffmpeg.writeFile(inputName, await fetchFile(blob));
      }

      const inputPath = isValidFile ? `${mountDir}/${safeFilename}` : inputName;

      // 2. Lancer ffmpeg sur le fichier (sans output) pour voir les infos dans les logs
      // On capture les logs temporairement
      let logs = '';
      const logHandler = ({ message }: { message: string }) => {
        logs += message + '\n';
      };
      ffmpeg.on('log', logHandler);

      try {
        await ffmpeg.exec(['-i', inputPath]);
      } catch (e) {
        // FFmpeg va error car pas d'output, c'est normal
      } finally {
        ffmpeg.off('log', logHandler);
        if (isValidFile) {
          try {
            await ffmpeg.unmount(mountDir);
            await ffmpeg.deleteDir(mountDir);
          } catch (e) { }
        } else {
          await this.safeDelete(inputName);
        }
      }

      // 3. Parser les logs pour trouver le FPS
      // Pattern: "Stream #0:0... 30 fps," ou "29.97 fps," ou "60 fps,"
      // Regex: /(\d+(?:\.\d+)?)\s+fps,/
      const fpsMatch = logs.match(/(\d+(?:\.\d+)?)\s+fps,/);

      if (fpsMatch && fpsMatch[1]) {
        const fps = parseFloat(fpsMatch[1]);
        console.log(`[FFmpegService] Detected FPS: ${fps}`);
        return fps;
      }

      console.warn('[FFmpegService] FPS detection failed, pattern not found.');
      return 0;

    } catch (error) {
      console.error('[FFmpegService] Error detecting FPS:', error);
      return 0;
    }
  }

  /**
   * Extraire l'audio d'un fichier lourd (ex: 6Go) sans le charger en RAM
   * Utilise le système de montage de fichier (WORKERFS)
   */
  async extractAudioFromLargeFile(file: File, onProgress?: (ratio: number) => void): Promise<Blob> {
    const ffmpeg = await this.getInstance();
    const uniqueId = Date.now().toString();
    const mountDir = `/mnt_large_${uniqueId}`; // Use unique dir to avoid collisions
    const inputFile = 'input.mp4';
    const outputName = `output_audio_${uniqueId}.mp3`; // Unique output name too

    console.log(`[FFmpegService] Large file extraction: ${file.name} (${(file.size / 1024 / 1024 / 1024).toFixed(2)} GB) in ${mountDir}`);

    try {
      // 1. Créer le dossier de montage
      try {
        await ffmpeg.createDir(mountDir);
      } catch (e) {
        // Should not happen with unique dir, but safe to ignore
      }

      // 2. Monter le fichier
      await ffmpeg.mount('WORKERFS' as any, {
        files: [file],
      }, mountDir);

      // Le chemin d'accès sera /mnt_large_file/<file.name>
      const inputPath = `${mountDir}/${file.name}`;

      console.log(`[FFmpegService] File mounted at ${inputPath}`);

      if (onProgress) {
        const progressHandler = ({ progress }: { progress: number }) => {
          onProgress(progress);
        };
        ffmpeg.on('progress', progressHandler);
        // Nettoyage du listener à la fin
        // Note: on le fait manuellement car .off n'est pas toujours fiable ou pratique ici
      }

      // 3. Exécuter la commande d'extraction
      // -ss 0 : forcer le démarrage EXACT à 0s (préserve le silence au début)
      // -map 0:a : mapper explicitement l'audio (évite les "optimisations" de timing)
      // -vn : désactiver la vidéo
      // -q:a 2 : qualité mp3 standard/haute (VBR)
      await ffmpeg.exec([
        '-ss', '0',           // IMPORTANT: Force start at exact 0, even with leading silence
        '-i', inputPath,
        '-map', '0:a',        // Explicitly map audio stream
        '-vn',
        '-af', 'aresample=async=1:first_pts=0', // FORCE timestamps to start at 0, filling gap with silence if needed
        '-acodec', 'libmp3lame',
        '-q:a', '2',
        outputName
      ]);

      // 4. Lire le résultat (le MP3 de sortie sera en RAM, mais il est beaucoup plus petit que la vidéo)
      const data = await ffmpeg.readFile(outputName);
      const blob = new Blob([data as any], { type: 'audio/mp3' });

      // 5. Cleanup
      try {
        // Enlève le fichier de sortie de la RAM FS
        await ffmpeg.deleteFile(outputName);
        // Démonte le fichier lourd
        await ffmpeg.unmount(mountDir);
        // Supprime le dossier
        await ffmpeg.deleteDir(mountDir);
      } catch (e) {
        console.warn('[FFmpegService] Cleanup warning:', e);
      }

      return blob;

    } catch (error) {
      console.error('[FFmpegService] Large file extraction failed:', error);
      // Tentative de cleanup en cas d'erreur
      try {
        await ffmpeg.unmount(mountDir);
        await ffmpeg.deleteDir(mountDir);
      } catch (e) { }
      throw error;
    }
  }

  /**
   * Tente une extraction audio rapide via FFmpeg.wasm (client-side)
   * Retourne null si l'extraction échoue (OOM, crash WASM, etc.)
   * Permet au code appelant de fallback vers extraction serveur
   */
  async tryExtractAudioFast(file: File, onProgress?: (ratio: number) => void): Promise<Blob | null> {
    try {
      console.log(`[FFmpegService] Tentative extraction rapide WASM pour ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)...`);
      const blob = await this.extractAudioFromLargeFile(file, onProgress);
      console.log(`[FFmpegService] ✅ Extraction WASM réussie !`);
      return blob;
    } catch (error) {
      console.warn(`[FFmpegService] ⚠️ Extraction WASM échouée, fallback serveur recommandé:`, error);
      return null;
    }
  }

  /**
   * Convert a WAV file (url or blob) to MP3 client-side
   * Used for enhanced audio: Cloud Function returns WAV, we convert to MP3 for smaller size + better browser compat
   */
  async convertWavToMp3(input: string | Blob): Promise<Blob> {
    const ffmpeg = await this.getInstance();
    const inputName = 'input_convert.wav';
    const outputName = 'output_convert.mp3';

    console.log('[FFmpegService] Converting WAV → MP3...');

    // Fetch if URL string
    let wavData: Blob;
    if (typeof input === 'string') {
      const response = await fetch(input);
      if (!response.ok) throw new Error(`Failed to fetch WAV: ${response.status}`);
      wavData = await response.blob();
    } else {
      wavData = input;
    }

    await ffmpeg.writeFile(inputName, await fetchFile(wavData));

    await ffmpeg.exec([
      '-i', inputName,
      '-vn',
      '-acodec', 'libmp3lame',
      '-b:a', '192k',
      '-ar', '44100',
      outputName
    ]);

    const data = await ffmpeg.readFile(outputName);
    const uint8Data = new Uint8Array(data as unknown as ArrayBufferLike);
    const blob = new Blob([uint8Data as any], { type: 'audio/mpeg' });

    console.log(`[FFmpegService] ✅ WAV→MP3 done (${(blob.size / 1024).toFixed(0)} KB)`);

    // Cleanup
    await this.safeDelete(inputName);
    await this.safeDelete(outputName);

    return blob;
  }

  private async safeDelete(filename: string): Promise<void> {
    if (!this.ffmpeg) return;
    try {
      await this.ffmpeg.deleteFile(filename);
    } catch (e) {
      // Ignore
    }
  }
}

// Export singleton instance
export const FFmpegService = new FFmpegServiceClass();

